import {useQuery} from '@tanstack/react-query';
import {useEffect} from 'react';
import {fetchCurrentUser} from '../api/auth.api';
import storage from '../utils/storage';
import {setupTokenRefreshInterval} from '../api/tokenRefreshInterceptor';
import Logger from '../utils/logger';

/**
 * Hook pour gérer l'authentification de l'utilisateur
 * Initialise le refresh automatique des tokens
 * @returns {Object} Objet contenant les informations utilisateur et l'état de chargement
 */
export const useAuth = () => {
    const token = storage.getToken();

    const { data: user, isLoading, error, refetch } = useQuery({
        queryKey: ['currentUser'],
        queryFn: fetchCurrentUser,
        enabled: !!token,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Initialiser le refresh automatique des tokens
    useEffect(() => {
        if (token) {
            Logger.log('[useAuth] Initialisation du refresh automatique des tokens');
            const intervalId = setupTokenRefreshInterval(5); // Vérifier toutes les 5 minutes
            
            return () => {
                clearInterval(intervalId);
                Logger.log('[useAuth] Nettoyage du refresh automatique');
            };
        }
    }, [token]);

    /**
     * Récupère le rôle de l'utilisateur depuis le token JWT
     * @returns {string|null} Le rôle de l'utilisateur ou null
     */
    const getUserRoleFromToken = () => {
        if (!token) return null;
        
        try {
            const decoded = storage.decodeToken();
            return decoded?.role || null;
        } catch (error) {
            return null;
        }
    };

    /**
     * Récupère les informations utilisateur depuis le token JWT
     * @returns {Object|null} Les informations utilisateur ou null
     */
    const getUserInfoFromToken = () => {
        if (!token) return null;
        
        try {
            const decoded = storage.decodeToken();
            return {
                username: decoded?.username,
                email: decoded?.email,
                role: decoded?.role,
                first_name: decoded?.first_name,
                last_name: decoded?.last_name,
            };
        } catch (error) {
            return null;
        }
    };

    const isAuthenticated = !!token;
    const role = user?.role || getUserRoleFromToken();
    const userInfo = user || getUserInfoFromToken();

    return {
        user: userInfo,
        role,
        isAuthenticated,
        isLoading,
        error,
        refetch,
    };
};

/**
 * Détermine la route du dashboard selon le rôle
 * @param {string} role - Le rôle de l'utilisateur (en majuscules ou minuscules)
 * @returns {string} La route du dashboard
 */
export const getDashboardRoute = (role) => {

    if (!role) return '/home';

    const normalizedRole = role.toUpperCase();

    switch (normalizedRole) {
        case 'ADMIN':
            return '/admin';
        case 'CREATOR':
            return '/home';
        case 'GERANT':
            return '/gerant/dashboard';
        case 'CLIENT':
        case 'PARTICIPANT':
            return '/participant';
        default:
            return '/home';
    }
};
